import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit3, Trash2, Eye, EyeOff, FileText } from "lucide-react";
import { adminApi, formatApiError } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return "—"; }
}

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true);
    adminApi.listBlogPosts()
      .then(setItems)
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer l'article "${title}" ?`)) return;
    try {
      await adminApi.deleteBlogPost(id);
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-blog">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Journal</p>
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Mon <span className="italic gold-text">blog.</span></h1>
          <p className="text-white/60 text-sm mt-3 max-w-2xl">
            Gérez vos articles SEO — créez, modifiez et publiez du contenu pour booster votre référencement local.
          </p>
        </div>
        <Link to="/admin/blog/nouveau" data-testid="admin-blog-new-btn" className="btn-gold inline-flex items-center gap-2">
          <Plus size={14} strokeWidth={1.5} /> Nouvel article
        </Link>
      </header>

      {err && <p className="text-sm text-red-300 mb-4">{err}</p>}

      <div className="card-luxe overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-white/50 italic">Chargement…</p>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <FileText size={36} strokeWidth={1} className="text-[#DCB457] mx-auto mb-4" />
            <p className="text-white/65">Aucun article pour l'instant.</p>
            <Link to="/admin/blog/nouveau" className="btn-ghost-gold mt-6 inline-flex">Créer le premier</Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-white/55">
              <tr>
                <th className="px-5 py-4 text-left">Titre</th>
                <th className="px-5 py-4 text-left">Statut</th>
                <th className="px-5 py-4 text-left">Publié le</th>
                <th className="px-5 py-4 text-left">Lecture</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-[#DCB457]/10" data-testid={`admin-blog-row-${p.slug}`}>
                  <td className="px-5 py-4">
                    <Link to={`/admin/blog/${p.id}`} className="text-white hover:text-[#DCB457] font-medium">
                      {p.title}
                    </Link>
                    <div className="text-[11px] text-white/40 mt-1">/blog/{p.slug}</div>
                  </td>
                  <td className="px-5 py-4">
                    {p.status === "published" ? (
                      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1">
                        <Eye size={11} strokeWidth={1.5} /> Publié
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-white/55 border border-white/15 px-2 py-1">
                        <EyeOff size={11} strokeWidth={1.5} /> Brouillon
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-white/65">{formatDate(p.published_at)}</td>
                  <td className="px-5 py-4 text-white/65">{p.reading_time_min || "—"} min</td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Link to={`/admin/blog/${p.id}`} data-testid={`admin-blog-edit-${p.slug}`} className="inline-flex items-center gap-1 text-[#DCB457] border border-[#DCB457]/30 px-2.5 py-1.5 hover:bg-[#DCB457]/10 transition text-xs">
                        <Edit3 size={12} strokeWidth={1.4} /> Éditer
                      </Link>
                      <button onClick={() => onDelete(p.id, p.title)} data-testid={`admin-blog-del-${p.slug}`} className="inline-flex items-center gap-1 text-red-300 border border-red-400/30 px-2.5 py-1.5 hover:bg-red-500/10 transition text-xs">
                        <Trash2 size={12} strokeWidth={1.4} /> Supp.
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
